import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle, Share2, ArrowRight, Calendar, FileDown } from "lucide-react";
import Footer from "@/components/Footer";
import ContactFormModal from "@/components/ContactFormModal";

import { API } from "@/lib/api";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, failed, expired
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [showContact, setShowContact] = useState(false);

  const pollStatusRef = useRef(null);

  pollStatusRef.current = async (sid, attempt) => {
    const maxAttempts = 15;
    if (attempt >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sid}`);
      const data = res.data;
      setPaymentData(data);

      if (data.payment_status === "paid" || data.status === "complete") {
        setStatus("paid");
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      const delay = attempt < 3 ? 2000 : 3000;
      setAttempts(attempt + 1);
      setTimeout(() => pollStatusRef.current(sid, attempt + 1), delay);
    } catch {
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollStatusRef.current(sid, attempt + 1), 3000);
      } else {
        setStatus("failed");
      }
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    pollStatusRef.current(sessionId, 0);
  }, [sessionId]);

  const shareText = encodeURIComponent(
    "I just ordered The Forty Year Bender by Chris Merlo. A raw, unfiltered story of addiction, rock bottom, and redemption. Launching June 4, 2026."
  );

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="payment-success-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
        </div>
      </nav>

      <main className="pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          {/* Checking */}
          {status === "checking" && (
            <div className="space-y-6 animate-fade-in" data-testid="payment-checking">
              <div className="flex justify-center">
                <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin" />
              </div>
              <h1 className="font-heading text-4xl tracking-tighter uppercase text-white">
                Confirming Your Payment...
              </h1>
              <p className="text-zinc-400">Please wait while we verify your payment. This takes just a moment.</p>
            </div>
          )}

          {/* Paid / Success */}
          {status === "paid" && (
            <>
              <div className="flex justify-center animate-fade-in">
                <div className="w-20 h-20 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
                </div>
              </div>

              <div className="space-y-4 opacity-0 animate-fade-in-up stagger-1" data-testid="payment-success-content">
                <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white">
                  Payment Confirmed
                </h1>
                <p className="text-zinc-300 text-base sm:text-lg">
                  Your order for <span className="text-[#D4AF37] font-semibold">
                    {paymentData?.metadata?.package_name || "The Forty Year Bender"}
                  </span> is confirmed.
                  {paymentData?.amount_total && (
                    <> Amount: <span className="text-white font-semibold">${(paymentData.amount_total / 100).toFixed(2)}</span></>
                  )}
                </p>
              </div>

              <div className="gold-line" />

              {/* eBook Download */}
              {paymentData?.metadata?.package_id === "digital" && (
                <div className="bg-[#121212] border border-[#D4AF37]/30 p-6 rounded-sm text-center space-y-4 opacity-0 animate-fade-in-up stagger-2">
                  <h3 className="font-heading text-xl tracking-tight uppercase text-white">Your eBook Is Ready</h3>
                  <p className="text-zinc-400 text-sm">Download your copy of The Forty Year Bender in EPUB format.</p>
                  <a
                    href="/downloads/The_Forty_Year_Bender.epub"
                    download="The_Forty_Year_Bender_Chris_Merlo.epub"
                    className="inline-flex items-center gap-3 btn-gold px-8 py-4 text-sm tracking-[0.1em] uppercase rounded-sm"
                    data-testid="ebook-download-button"
                  >
                    <FileDown className="w-5 h-5" /> Download eBook (EPUB)
                  </a>
                  <p className="text-zinc-600 text-xs">A download link has also been sent to your email.</p>
                </div>
              )}

              {/* What Happens Next */}
              <div className="bg-[#121212] border border-white/10 p-8 sm:p-10 rounded-sm space-y-6 text-left opacity-0 animate-fade-in-up stagger-2">
                <h2 className="font-heading text-2xl tracking-tight uppercase text-white">What Happens Next</h2>
                <div className="space-y-5">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="font-heading text-sm text-[#D4AF37]">1</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Confirmation Email</p>
                      <p className="text-zinc-400 text-sm">A receipt and order confirmation will arrive in your inbox shortly.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="font-heading text-sm text-[#D4AF37]">2</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Launch Day &mdash; June 4, 2026
                      </p>
                      <p className="text-zinc-400 text-sm">
                        {paymentData?.metadata?.package_id === "digital"
                          ? "Your digital copy will be delivered to your email on launch day."
                          : "Your book will be shipped on launch day with priority delivery."}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="font-heading text-sm text-[#D4AF37]">3</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Bonuses Included</p>
                      <p className="text-zinc-400 text-sm">
                        {paymentData?.metadata?.package_id === "signed"
                          ? "Chris will personally sign your copy with a handwritten note. Your Clarity Mini-Guide and Q&A invite are on the way."
                          : paymentData?.metadata?.package_id === "digital"
                          ? "Your digital copy comes with lifetime access across all devices."
                          : "Your free Clarity Mini-Guide and exclusive Q&A invite will arrive by email."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Share */}
              <div className="space-y-4 opacity-0 animate-fade-in-up stagger-3">
                <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Spread The Word</p>
                <p className="text-zinc-400 text-sm">Know someone who needs this book? Share it.</p>
                <div className="flex justify-center gap-4">
                  <a
                    href={`https://twitter.com/intent/tweet?text=${shareText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-white/20 text-white px-6 py-3 text-xs tracking-[0.15em] uppercase rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all inline-flex items-center gap-2"
                    data-testid="share-twitter"
                  >
                    <Share2 className="w-4 h-4" /> Share on X
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?quote=${shareText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-white/20 text-white px-6 py-3 text-xs tracking-[0.15em] uppercase rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all inline-flex items-center gap-2"
                    data-testid="share-facebook"
                  >
                    <Share2 className="w-4 h-4" /> Share on Facebook
                  </a>
                </div>
              </div>
            </>
          )}

          {/* Failed / Error */}
          {(status === "failed" || status === "expired") && (
            <div className="space-y-6 animate-fade-in" data-testid="payment-failed">
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
                </div>
              </div>
              <h1 className="font-heading text-4xl tracking-tighter uppercase text-white">
                {status === "expired" ? "Session Expired" : "Payment Processing"}
              </h1>
              <p className="text-zinc-400 text-base max-w-md mx-auto">
                {status === "expired"
                  ? "This payment session has expired. Please try again."
                  : "Your payment may still be processing. If you were charged, your order has been received and you'll get a confirmation email. If not, you can try again below."}
              </p>
              {sessionId && (
                <button
                  onClick={() => { setStatus("checking"); pollStatus(sessionId, 0); }}
                  className="inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-6 py-3 text-xs tracking-[0.15em] uppercase rounded-sm hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="payment-recheck-button"
                >
                  Check Payment Status Again
                </button>
              )}
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm"
                data-testid="payment-retry-button"
              >
                Try Again <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-zinc-600 text-xs">
                Need help? <button onClick={() => setShowContact(true)} className="text-[#D4AF37] hover:underline">Get in touch</button>
              </p>
            </div>
          )}

          {/* Back to home */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-[#D4AF37] transition-colors"
            data-testid="payment-back-home"
          >
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Home
          </Link>
        </div>
      </main>

      <Footer />
      <ContactFormModal open={showContact} onClose={() => setShowContact(false)} />
    </div>
  );
}
